import React, { useEffect, useState } from 'react';
import { FormInstance } from 'antd/lib/form';
import { Form, Row, Col, InputNumber, Select, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import FormListAddButton from '../FormListAddButton';
import { ClientFeatureConfig } from '../../types/Feature';
import {
    SmartFleetChoiceAttributes,
    SmartFleetType,
} from '../../../../server/src/models/featureConfigAttributes/SmartFleetChoiceAttributes';
import JavascriptCodeInput from '../Shared/Inputs/JavascriptCodeInput';
import FeatureStatusInput from '../Shared/Inputs/FeatureStatusInput';

const { Option } = Select;
const cardHeaderTitleStyle = { fontSize: '18px' };

interface SmartFleetChoiceSectionProps {
    formInstance: FormInstance;
    smartFleetChoiceConfig?: ClientFeatureConfig<SmartFleetChoiceAttributes>;
    isDetailMode?: boolean;
    enableControls: boolean;
    // eslint-disable-next-line react/no-unused-prop-types
    resetFormFlag?: boolean;
}

interface SFCFleetInputProps {
    isDetailMode?: boolean;
    fieldKey: number;
    fieldName: number;
    config: SmartFleetChoiceAttributes | undefined;
    remove?: () => void;
}

const SFCFleetInput: React.FC<SFCFleetInputProps> = ({
    isDetailMode,
    fieldKey,
    fieldName,
    config,
    remove,
}: SFCFleetInputProps) => {
    const options = [
        { label: 'On-demand', value: SmartFleetType.ONDEMAND },
        { label: 'Dedicated', value: SmartFleetType.DEDICATED },
    ];

    return (
        <Row>
            <Col span={isDetailMode ? 7 : 6}>
                <Form.Item label="Fleet Id" name={[fieldName, 'sfcFleetId']}>
                    {isDetailMode ? <span>{config?.fleets[fieldName]?.id}</span> : <InputNumber />}
                </Form.Item>
            </Col>
            <Col span={17}>
                <Form.Item label="Fleet type" name={[fieldName, 'sfcFleetType']}>
                    {isDetailMode ? (
                        <span>{config?.fleets[fieldName]?.type}</span>
                    ) : (
                        <Select>
                            {options.map(({ label: optLab, value: val }) => (
                                <Option key={val} value={val}>
                                    {optLab}
                                </Option>
                            ))}
                        </Select>
                    )}
                </Form.Item>
            </Col>
            {!isDetailMode && (
                <Col span={1}>
                    <span className="ant-form-text text-secondary" style={{ marginTop: '5px', cursor: 'pointer' }}>
                        <Tag
                            color="red"
                            style={{ cursor: 'pointer' }}
                            onClick={(): void => remove?.()}
                            id={`sfc_delete_${fieldKey}`}
                        >
                            <DeleteOutlined />
                        </Tag>
                    </span>
                </Col>
            )}
        </Row>
    );
};

const SmartFleetChoiceSection: React.FC<SmartFleetChoiceSectionProps> = (props: SmartFleetChoiceSectionProps) => {
    const { isDetailMode, smartFleetChoiceConfig: sfcConfig, formInstance, enableControls } = props;
    const [isFeatureDisabled, setIsFeatureDisabled] = useState(!sfcConfig?.config.enabled);

    useEffect(() => {
        if (sfcConfig) {
            formInstance.setFields([
                {
                    name: 'sfcFleets',
                    value: sfcConfig.config.fleets?.map((elem) => ({
                        sfcFleetType: elem.type,
                        sfcFleetId: elem.id,
                    })),
                },
                { name: 'sfcEnabled', value: sfcConfig.config.enabled },
                { name: 'sfcFunction', value: sfcConfig.config.customJSLogic },
            ]);
        }
    }, [sfcConfig]);

    return (
        <>
            <div className="card border-0" id="json-transform-section">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center bg-white">
                    <p className="m-0 p-0 h5" style={cardHeaderTitleStyle}>
                        Smart Fleet Choice
                    </p>
                    <div className="d-inline-flex align-items-center">
                        <span className="mr-2 d-none d-sm-block">Feature is:</span>
                        <FeatureStatusInput
                            name="sfcEnabled"
                            id="sfc_enabled"
                            isDetailMode={isDetailMode}
                            enabled={sfcConfig?.config.enabled}
                            isFeatureDisabled={isFeatureDisabled}
                            onChange={(value): void => {
                                setIsFeatureDisabled(!value);
                            }}
                            enableControls={enableControls}
                        />
                    </div>
                </div>
            </div>
            <div className="card-body">
                <Row gutter={24}>
                    <Col span={24}>
                        <JavascriptCodeInput
                            isDetailMode={isDetailMode}
                            fieldName="sfcFunction"
                            label="Custom Logic"
                            functionSignature="function customLogic(payload, etas, distanceAndTime)"
                            isFeatureDisabled={isFeatureDisabled}
                            enableControls={enableControls}
                            jsCode={sfcConfig?.config.customJSLogic}
                        />
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.List name="sfcFleets">
                            {(fields, { add, remove }): JSX.Element => (
                                <>
                                    {fields.map((field) => (
                                        <SFCFleetInput
                                            key={field.key}
                                            isDetailMode={isDetailMode}
                                            fieldKey={field.key}
                                            fieldName={field.name}
                                            config={sfcConfig?.config}
                                            remove={(): void => {
                                                remove(field.name);
                                            }}
                                        />
                                    ))}
                                    <FormListAddButton
                                        isDetailMode={isDetailMode || false}
                                        add={add}
                                        text="Add a Fleet"
                                    />
                                </>
                            )}
                        </Form.List>
                    </Col>
                </Row>
            </div>
        </>
    );
};

export default SmartFleetChoiceSection;
